<script>
import EmojiGlyph from 'shared/components/emoji/EmojiGlyph.vue';
import { rememberRecentReaction } from 'shared/helpers/recentReactions';

export default {
  name: 'MessageReactionBar',
  components: { EmojiGlyph },
  props: {
    large: {
      type: Boolean,
      default: true,
    },
    // Aceleres: no modal de reacoes a barra e so pra trocar a propria
    // reacao, entao o "+" (que abre o seletor completo ancorado no balao)
    // nao faz sentido ali. Default false mantem o comportamento de sempre.
    hideMore: {
      type: Boolean,
      default: false,
    },
    channelType: {
      type: String,
      default: '',
    },
  },
  emits: ['react', 'more', 'close'],
  computed: {
    quickEmojis() {
      if (this.channelType === 'Channel::Instagram') return ['❤️'];

      // Pedido 2026-09-14 (facundo): no telemovel sao 12, com 7 visiveis e o
      // resto a deslizar com o dedo. No computador continuam as 6 primeiras
      // (`md:hidden` nas restantes, no template).
      return [
        '👍',
        '❤️',
        '😂',
        '😮',
        '😢',
        '🙏',
        '🔥',
        '🎉',
        '😍',
        '👏',
        '🤔',
        '😡',
      ];
    },
  },
  data() {
    return {
      visibleWidth: null,
      labels: {
        toolbar: 'Reações rápidas',
        reactWith: 'Reagir com',
        more: 'Mais reações',
      },
    };
  },
  mounted() {
    this.measureVisibleWidth();
  },
  methods: {
    // As medidas dos botoes mudam conforme o sitio (pill da mensagem,
    // galeria, PDF, anexos), por isso a largura das 7 visiveis e medida no
    // proprio DOM: do inicio do 1.o botao ao inicio do 8.o, menos o vao.
    measureVisibleWidth() {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      const buttons = this.$refs.scroller?.children;
      if (!buttons || buttons.length < 8) return;
      const gap = buttons[1].offsetLeft - buttons[0].offsetLeft - buttons[0].offsetWidth;
      this.visibleWidth = `${buttons[7].offsetLeft - buttons[0].offsetLeft - gap}px`;
    },
    reactWith(emoji) {
      rememberRecentReaction(emoji);
      this.$emit('react', emoji);
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.$emit('close');
        return;
      }

      const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!navigationKeys.includes(event.key)) return;

      const buttons = [...event.currentTarget.querySelectorAll('button')];
      const currentIndex = buttons.indexOf(event.target.closest('button'));
      if (currentIndex < 0) return;

      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      } else if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = buttons.length - 1;
      }
      buttons[nextIndex]?.focus();
    },
  },
};
</script>

<template>
  <div
    class="aceleres-reaction-bar flex items-center px-0.5 py-1"
    role="toolbar"
    :aria-label="labels.toolbar"
    @keydown="handleKeydown"
  >
    <div
      ref="scroller"
      class="flex items-center -space-x-px max-md:-my-1 max-md:max-w-[var(--acl-reaction-visible-width)] max-md:snap-x max-md:snap-mandatory max-md:overflow-x-auto max-md:overscroll-x-contain max-md:py-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden"
      :style="
        visibleWidth ? { '--acl-reaction-visible-width': visibleWidth } : {}
      "
    >
      <button
        v-for="(emoji, index) in quickEmojis"
        :key="emoji"
        type="button"
        class="aceleres-emoji-btn text-[1.75rem] leading-none h-6 w-6 flex flex-shrink-0 items-center justify-center rounded-full hover:bg-n-alpha-1 hover:scale-125 transition-all duration-150 max-md:snap-start"
        :class="[
          {
            '!h-10 !w-10': large && channelType !== 'Channel::Instagram',
            'md:hidden': index >= 6,
          },
          channelType === 'Channel::Instagram'
            ? '!h-9 !w-9 !text-2xl !leading-none !text-n-slate-11 hover:!scale-100'
            : '',
        ]"
        :aria-label="
          channelType === 'Channel::Instagram'
            ? 'Reagir com coração'
            : `${labels.reactWith} ${emoji}`
        "
        @click.stop="reactWith(emoji)"
      >
        <span v-if="channelType === 'Channel::Instagram'" aria-hidden="true">
          ♥︎
        </span>
        <EmojiGlyph
          v-else
          :emoji="emoji"
          :class="{ '!h-10 !w-10 !text-[2.5rem]': large }"
        />
      </button>
    </div>
    <button
      v-if="!hideMore && channelType !== 'Channel::Instagram'"
      type="button"
      class="aceleres-emoji-btn aceleres-emoji-btn--more h-6 w-6 flex items-center justify-center rounded-full text-n-slate-11 transition-all duration-150"
      :class="{ '!h-10 !w-10': large }"
      :aria-label="labels.more"
      @click.stop="$emit('more', $event)"
    >
      <span class="i-lucide-plus w-4 h-4" />
    </button>
  </div>
</template>
